/**
 * The typed socket to the game server.
 *
 * A dropped socket is not reopened here. Whether and when to try again is the
 * app's call, and rejoining a game is a protocol step rather than a transport
 * one: the server holds the game for a while, and the app presents the resume
 * token on the new connection to pick it back up.
 */

import type { ClientMessage, ReplacedCloseCode, ServerMessage } from "../server/protocol.js";
import { getWebSocketUrl } from "./ws-url.js";

/** `replaced` is a close the server made because another connection took the game. */
export type ConnectionStatus = "closed" | "connecting" | "open" | "replaced";

const REPLACED_CLOSE_CODE: ReplacedCloseCode = 4000;

export interface ConnectionHandlers {
    readonly onMessage: (message: ServerMessage) => void;
    readonly onStatus: (status: ConnectionStatus) => void;
}

export interface Connection {
    send(message: ClientMessage): void;
    /** Opens a new socket in place of one that closed; a live socket is left alone. */
    reconnect(): void;
    close(): void;
}

function isServerMessage(value: unknown): value is ServerMessage {
    return typeof value === "object" && value !== null && typeof Reflect.get(value, "type") === "string";
}

export function connect(handlers: ConnectionHandlers, url = getWebSocketUrl(window.location)): Connection {
    function open(): WebSocket {
        const next = new WebSocket(url);
        handlers.onStatus("connecting");

        // A failed socket always follows its error with a close, so the close
        // alone reports the drop and the app hears about it exactly once.
        next.addEventListener("open", () => handlers.onStatus("open"));
        next.addEventListener("close", (event) => {
            handlers.onStatus(event.code === REPLACED_CLOSE_CODE ? "replaced" : "closed");
        });

        next.addEventListener("message", (event: MessageEvent<string>) => {
            try {
                const parsed: unknown = JSON.parse(event.data);

                if (isServerMessage(parsed)) {
                    handlers.onMessage(parsed);
                }
            }
            catch {
                // A frame the server did not send, or one mangled in transit. There
                // is nothing useful to show the player, so it is dropped.
            }
        });

        return next;
    }

    let socket = open();

    return {
        send(message: ClientMessage): void {
            if (socket.readyState === WebSocket.OPEN) {
                socket.send(JSON.stringify(message));
            }
        },

        reconnect(): void {
            if (socket.readyState === WebSocket.CLOSED) {
                socket = open();
            }
        },

        close(): void {
            socket.close();
        }
    };
}
